import { memo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { isEmpty, noop } from 'lodash';
// Import Enums
import { LabelAppearanceEnum } from 'enums';
// Import UI Components
import { Tooltip, Button, Popover, SkeletonLoader, Icon } from '@geneui/components';
// Import Components
import OptionListContent, { type OptionListContentProps } from './OptionListContent';
// Import Hooks
import useOnClickOutside from 'hooks/UseOnClickOutside';
// Import Styles
import 'assets/scss/optionList.scss';

export interface OptionListProps extends OptionListContentProps {
    optionLabel?: React.ReactNode;
    placeholder?: string | React.ReactNode;
    toggleHandler: () => void;
    isVisible?: boolean;
    setIsVisible?: (visible: boolean) => void;
    tooltipText?: string;
    popoverClassName?: string;
    buttonClassName?: string;
    buttonWrapperClassName?: string;
    popoverMinHeight?: number;
    popoverMaxHeight?: number;
    contentWrapperClassName?: string;
    openIcon?: string;
    closeIcon?: string;
    clearable?: boolean;
    onClear?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    isLoading?: boolean;
    isLoadingList?: boolean;
    isValid?: boolean;
    errorText?: Nullable<string>;
    labelAppearance?: LabelAppearanceEnum;
    withInfoIcon?: boolean;
    infoIconTitle?: string;
    isDisabled?: boolean;
}

const OptionList = (props: OptionListProps) => {
    const {
        optionLabel = '',
        placeholder = '',
        toggleHandler,
        isVisible = false,
        setIsVisible = noop,
        tooltipText = '',
        popoverClassName = '',
        buttonClassName = '',
        buttonWrapperClassName = '',
        popoverMinHeight = 332,
        popoverMaxHeight = 252,
        contentWrapperClassName = '',
        openIcon = 'bc-icon-arrow-down',
        closeIcon = 'bc-icon-arrow-up',
        clearable = false,
        onClear = noop,
        isLoading = false,
        isLoadingList = false,
        isValid = true,
        errorText = '',
        labelAppearance = LabelAppearanceEnum.None,
        withInfoIcon = false,
        infoIconTitle = '',
        isDisabled = false,
        ...rest
    } = props;
    const optionListRef = useRef<HTMLDivElement | null>(null);

    useOnClickOutside(
        optionListRef,
        useCallback(() => {
            if (isVisible) {
                setIsVisible(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isVisible]),
    );

    const clearClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onClear(e);
    };

    return (
        <SkeletonLoader isBusy={isLoading}>
            {!isLoading && (
                <div className="crm-cj-visualization-step-dropdown-item" ref={optionListRef}>
                    <Popover
                        isOpen={isVisible && !isLoading}
                        extendTargetWidth={false}
                        className={classNames('crm-option-list-popover', { [popoverClassName]: popoverClassName })}
                        minHeight={popoverMinHeight}
                        maxHeight={popoverMaxHeight}
                        scrollbarNeeded={false}
                        align="end"
                    >
                        <div
                            className={classNames('crm-option-list-content-wrapper', {
                                [contentWrapperClassName]: contentWrapperClassName,
                            })}
                        >
                            <div className={buttonWrapperClassName}>
                                <Tooltip text={tooltipText} position="bottom">
                                    <Button
                                        className={classNames('crm-option-list-content__toggle-button', {
                                            [buttonClassName]: buttonClassName,
                                            'crm-option-list-content__is-not-valid': !isValid,
                                            'crm-option-list-content__placeholder':
                                                labelAppearance === LabelAppearanceEnum.Swap &&
                                                isEmpty(optionLabel) &&
                                                placeholder,
                                            'crm-option-list-content__clearable': clearable,
                                        })}
                                        onClick={toggleHandler}
                                        icon={isVisible ? closeIcon : openIcon}
                                        disabled={isDisabled}
                                    >
                                        {placeholder && labelAppearance === LabelAppearanceEnum.Swap && (
                                            <div className="input-element-back crm-option-list-content__toggle-button__label">
                                                <div className="input-swap-label">
                                                    <div className="input-swap">
                                                        <div
                                                            className={classNames('input-swap-animate ellipsis-text', {
                                                                hide: isEmpty(optionLabel) && optionLabel !== 0,
                                                            })}
                                                        >
                                                            <span className="ellipsis-text">{placeholder}</span>
                                                            {withInfoIcon && (
                                                                <Tooltip title={infoIconTitle}>
                                                                    <Icon type="bc-icon-info" />
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {isEmpty(optionLabel) ? placeholder : optionLabel}
                                        {clearable && !isEmpty(optionLabel) && (
                                            <Button
                                                className="crm-option-list-content__clear-button"
                                                color="default"
                                                appearance="minimal"
                                                icon="bc-icon-clear-small"
                                                onClick={clearClickHandler}
                                            />
                                        )}
                                    </Button>
                                </Tooltip>
                                <span className="crm-option-list-content-wrapper__error-text">{errorText}</span>
                            </div>
                            {isVisible && <OptionListContent {...rest} isLoading={isLoadingList} />}
                        </div>
                    </Popover>
                </div>
            )}
        </SkeletonLoader>
    );
};

export default memo(OptionList);
